
import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import { useNavigate } from 'react-router-dom';
import '../styles.css';

const CreateUsers = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState({}); 
  const [currentUserId] = useState(localStorage.getItem('id')); 
  const [searchTerm, setSearchTerm] = useState('');
  const [newUser, setNewUser] = useState({}); 
  const [workLocations, setWorkLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get('/user/')
      .then(response => {
        const usersData = response.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          role: user.role,
          employeeCode: user.employee_code
        }));
        setUsers(usersData);

        axiosInstance.get(`/user/${currentUserId}/`)
          .then(response => {
            const userData = response.data;
            if (userData.role) {
              setCurrentUser({ ...userData, role: userData.role.name });
            } else {
              const fallbackRole = userData.role?.[0]?.name || 'guest';
              setCurrentUser({ ...userData, role: fallbackRole });
            }
          })
          .catch(console.error);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const filteredEmployees = users.filter((employee) => {
    const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) || 
      employee.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleLogout = () => {
    axiosInstance.post('/logout/', { refresh: localStorage.getItem('refresh') })
      .then(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('employee_code');
        localStorage.removeItem('assignedRole');
        localStorage.removeItem('id'); 
        window.location.href = '/';
      })
      .catch(console.error);
  }; 

  const handleCreateUser = (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    const formData = new FormData();
    formData.append('profile_photo', profilePhoto);
    Object.keys(newUser).forEach((key) => {
      formData.append(key, newUser[key]);
    });
    
    axiosInstance.post('/register/', formData)
      .then(response => {
        setSuccess('User created successfully!');
        setUsers([...users, response.data]);
        setNewUser({}); 
        setTimeout(() => {
          navigate(`/assign/${response.data.id}`); 
        }, 1500);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to create user. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    axiosInstance.get('/office-branch/')
      .then(response => {
        setWorkLocations(response.data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get('/role/')
      .then(response => {
        setRoles(response.data);
      })
      .catch(console.error);
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleProfilePhotoChange = (event) => {
    setProfilePhoto(event.target.files[0]);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    setProfilePhoto(event.dataTransfer.files[0]);
  };

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>👤 Create New User</h1>
          <p>Add a new employee to your organization</p>
        </div>

        <div className="create-user-card">
          {error && (
            <div className="form-error-message">
              <span>⚠️</span> {error}
            </div>
          )}
          
          {success && (
            <div className="form-success-message">
              <span>✓</span> {success}
            </div>
          )}

          <form onSubmit={handleCreateUser}>
            <div className="form-section">
              <div className="section-header">
                <span className="section-icon">👤</span>
                <h3>Personal Information</h3>
              </div>
              
              <div className="form-grid">
                <div className="form-group photo-upload-group">
                  <label>Profile Photo</label>
                  <div className="photo-upload-container">
                    <div className="photo-preview">
                      {profilePhoto ? (
                        <img src={URL.createObjectURL(profilePhoto)} alt="Profile" />
                      ) : (
                        <div className="photo-placeholder">
                          <span>📷</span>
                          <p>No photo selected</p>
                        </div>
                      )}
                    </div>
                    <input 
                      type="file" 
                      name="profile_photo" 
                      id="profile_photo" 
                      onChange={handleProfilePhotoChange}
                      accept="image/*"
                    />
                    <div 
                      className={`dropzone ${dragging ? 'dragging' : ''}`}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {dragging ? (
                        <p>Drop the file here...</p>
                      ) : (
                        <p>Drag & drop or click to select</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input 
                    type="text" 
                    name="first_name" 
                    id="first_name" 
                    value={newUser.first_name || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="middle_name">Middle Name</label>
                  <input 
                    type="text" 
                    name="middle_name" 
                    id="middle_name" 
                    value={newUser.middle_name || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name *</label>
                  <input 
                    type="text" 
                    name="last_name" 
                    id="last_name" 
                    value={newUser.last_name || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    id="email" 
                    value={newUser.email || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_number">Contact Number *</label>
                  <input 
                    type="text" 
                    name="contact_number" 
                    id="contact_number" 
                    value={newUser.contact_number || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select name="gender" id="gender" value={newUser.gender || ''} onChange={handleInputChange}>
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="date_of_birth">Date of Birth</label>
                  <input 
                    type="date" 
                    name="date_of_birth" 
                    id="date_of_birth" 
                    value={newUser.date_of_birth || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="blood_group">Blood Group</label>
                  <select name="blood_group" id="blood_group" value={newUser.blood_group || ''} onChange={handleInputChange}>
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="marital_status">Marital Status</label>
                  <select name="marital_status" id="marital_status" value={newUser.marital_status || ''} onChange={handleInputChange}>
                    <option value="">Select Status</option>
                    <option value="unmarried">Unmarried</option>
                    <option value="married">Married</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="nationality">Nationality</label>
                  <input 
                    type="text" 
                    name="nationality" 
                    id="nationality" 
                    value={newUser.nationality || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="religion">Religion</label>
                  <input 
                    type="text" 
                    name="religion" 
                    id="religion" 
                    value={newUser.religion || ''} 
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <span className="section-icon">🏢</span>
                <h3>Official Information</h3>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="employee_code">Employee Code *</label>
                  <input 
                    type="text" 
                    name="employee_code" 
                    id="employee_code" 
                    value={newUser.employee_code || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="official_email_id">Official Email</label>
                  <input 
                    type="email" 
                    name="official_email_id" 
                    id="official_email_id" 
                    value={newUser.official_email_id || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="personal_email_id">Personal Email</label>
                  <input 
                    type="email" 
                    name="personal_email_id" 
                    id="personal_email_id" 
                    value={newUser.personal_email_id || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="duty_of">Duty Of</label>
                  <input 
                    type="text" 
                    name="duty_of" 
                    id="duty_of" 
                    value={newUser.duty_of || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="esic_no">ESIC No.</label>
                  <input 
                    type="text" 
                    name="esic_no" 
                    id="esic_no" 
                    value={newUser.esic_no || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uan_no">UAN No.</label>
                  <input 
                    type="text" 
                    name="uan_no" 
                    id="uan_no" 
                    value={newUser.uan_no || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <input 
                    type="text" 
                    name="department" 
                    id="department" 
                    value={newUser.department || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="designation">Designation</label>
                  <input 
                    type="text" 
                    name="designation" 
                    id="designation" 
                    value={newUser.designation || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="work_anniversary">Work Anniversary</label>
                  <input 
                    type="date" 
                    name="work_anniversary" 
                    id="work_anniversary" 
                    value={newUser.work_anniversary || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="permanent_address">Permanent Address</label>
                  <input 
                    type="text" 
                    name="permanent_address" 
                    id="permanent_address" 
                    value={newUser.permanent_address || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mailing_address">Mailing Address</label>
                  <input 
                    type="text" 
                    name="mailing_address" 
                    id="mailing_address" 
                    value={newUser.mailing_address || ''} 
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input 
                    type="password" 
                    name="password" 
                    id="password" 
                    value={newUser.password || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm Password *</label>
                  <input 
                    type="password" 
                    name="confirm_password" 
                    id="confirm_password" 
                    value={newUser.confirm_password || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span>➕</span> Create User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateUsers;

